/**
 * Phase 19 — Shared permission tree (groups → sub-pages → keys).
 *
 * The same component drives:
 *   • UsersPage "Manage Permissions" drawer (3-state: Inherit / Allow / Deny)
 *   • Role Permissions page (2-state: Allow / Deny)
 *
 * Groups + labels mirror the sidebar nav so admin sees a familiar tree.
 */
import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

// Pretty labels by key suffix and prefix. Falls back to humanizing the key.
const PRETTY = {
  view: "View",
  create: "Create",
  edit: "Edit",
  delete: "Delete",
  approve: "Approve",
  reject: "Reject",
  submit: "Submit",
  upload: "Upload",
  export: "Export",
  download: "Download",
  post: "Post to GL",
  post_gl: "Post to GL",
  review: "Review",
  verify: "Verify",
  process: "Process",
  delete_posted: "Delete posted",
  reset_password: "Reset password",
  manage_permissions: "Manage permissions",
  fund: "Fund",
  spend: "Spend",
  payment: "Record payment",
  create_filing: "Create filing",
  create_payment: "Record payment",
  create_account: "Create account",
  edit_account: "Edit account",
  deactivate_account: "Deactivate account",
  delete_account: "Delete account",
  create_deposit: "Create deposit",
  create_withdrawal: "Create withdrawal",
  view_reports: "View reports",
  section: "Section",
  access: "Access",
};

function labelize(token) {
  if (PRETTY[token]) return PRETTY[token];
  return token.split("_").map((w) => w[0]?.toUpperCase() + w.slice(1)).join(" ");
}

export function groupKeys(keys) {
  // Group canonical "group.sub.action" into nested tree.
  const tree = {};
  for (const k of keys) {
    const parts = k.split(".");
    const grp = parts[0];
    const sub = parts.length > 2 ? parts.slice(1, -1).join(".") : "_root_";
    const action = parts[parts.length - 1];
    if (!tree[grp]) tree[grp] = {};
    if (!tree[grp][sub]) tree[grp][sub] = [];
    tree[grp][sub].push({ key: k, action });
  }
  return tree;
}

export function PermissionTree({
  keys,
  values, // { key: true | false | undefined }  ("undefined" = inherit for 3-state)
  roleDefaults, // { key: bool } — used only by 3-state mode to show inherit value
  threeState = false,
  onChange,
  searchable = true,
}) {
  const [openGroups, setOpenGroups] = useState({});
  const [q, setQ] = useState("");
  const tree = useMemo(() => groupKeys(keys), [keys]);

  const matches = (k) => !q || k.toLowerCase().includes(q.toLowerCase());

  const setKey = (k, v) => onChange?.(k, v);

  const stateOf = (k) => {
    const v = values?.[k];
    if (threeState) {
      if (v === undefined || v === null) return "inherit";
      return v ? "allow" : "deny";
    }
    return v ? "allow" : "deny";
  };

  return (
    <div className="space-y-3">
      {searchable && (
        <input
          type="text"
          placeholder="Search permissions…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="perm-tree-search"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#4FA3D1] focus:outline-none"
        />
      )}
      <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2">
        {Object.entries(tree).map(([grp, subs]) => {
          const allInGroup = Object.values(subs).flat().map((x) => x.key);
          const visibleKeys = allInGroup.filter(matches);
          if (!visibleKeys.length) return null;
          const isOpen = openGroups[grp] ?? true;
          return (
            <div key={grp} className="rounded-lg border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => setOpenGroups({ ...openGroups, [grp]: !isOpen })}
                data-testid={`perm-group-${grp}`}
                className="flex w-full items-center justify-between px-3 py-2 text-left"
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  {labelize(grp)}
                </span>
                <Badge variant="outline" className="text-xs">{visibleKeys.length}</Badge>
              </button>
              {isOpen &&
                Object.entries(subs).map(([sub, items]) => {
                  const visible = items.filter((x) => matches(x.key));
                  if (!visible.length) return null;
                  return (
                    <div key={sub} className="border-t border-slate-100 px-3 py-2">
                      {sub !== "_root_" && (
                        <div className="mb-1 text-xs uppercase tracking-wide text-slate-500">
                          {labelize(sub.replace(/\./g, " · "))}
                        </div>
                      )}
                      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                        {visible.map(({ key, action }) => {
                          const state = stateOf(key);
                          const inheritFrom = threeState && state === "inherit"
                            ? (roleDefaults?.[key] ? "Allowed by role" : "Denied by role")
                            : null;
                          return (
                            <div
                              key={key}
                              data-testid={`perm-row-${key}`}
                              className="flex items-center justify-between rounded-md border border-slate-100 px-2 py-1.5 text-sm hover:bg-slate-50"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-slate-700">{labelize(action)}</div>
                                <div className="truncate text-xs text-slate-400">{key}</div>
                                {inheritFrom && (
                                  <div className="text-xs italic text-slate-400">{inheritFrom}</div>
                                )}
                              </div>
                              {threeState ? (
                                <ThreeStateToggle
                                  state={state}
                                  testid={key}
                                  onChange={(s) =>
                                    setKey(key, s === "inherit" ? undefined : s === "allow")
                                  }
                                />
                              ) : (
                                <TwoStateToggle
                                  allow={state === "allow"}
                                  testid={key}
                                  onToggle={() => setKey(key, !(state === "allow"))}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ThreeStateToggle({ state, onChange, testid }) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
      {[
        { v: "inherit", label: "Inherit", cls: "text-slate-600" },
        { v: "allow", label: "Allow", cls: "text-emerald-700" },
        { v: "deny", label: "Deny", cls: "text-rose-700" },
      ].map((o) => (
        <button
          key={o.v}
          type="button"
          data-testid={`perm-toggle-${testid}-${o.v}`}
          onClick={() => onChange(o.v)}
          className={`px-2.5 py-1 text-xs font-medium ${o.cls} ${
            state === o.v ? "bg-slate-100 font-semibold" : "bg-white hover:bg-slate-50"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function TwoStateToggle({ allow, onToggle, testid }) {
  return (
    <Button
      type="button"
      size="sm"
      variant={allow ? "default" : "outline"}
      onClick={onToggle}
      data-testid={`perm-toggle-${testid}`}
      className={
        allow
          ? "h-7 bg-emerald-600 text-white hover:bg-emerald-700"
          : "h-7 text-rose-600"
      }
    >
      {allow ? "Allow" : "Deny"}
    </Button>
  );
}
